import * as React from 'react';
import { useMemo, useEffect, useState, useRef } from 'react';
import { Toast, ToastBody, ToastHeader, Table, Input, FormGroup, FormFeedback, Label, TabContent, TabPane, Nav, NavItem, NavLink, Card, Button, CardTitle, CardText, Row, Col, CardBody } from 'reactstrap';
import Page from '../Page';
import { useAppSelector, useAppDispatch } from '../../hooks/index';
import DropdownTreeSelect from 'react-dropdown-tree-select'
import 'react-dropdown-tree-select/dist/styles.css'
import { Concept, ConceptRef, getAccountId, getPublicName } from '../../redux/ConceptSlice';
import { DtsConcept, isNewConcept, loadTaxonomyConcept, resetTaxonomyConceptState, setSelectedTaxonomyConcept, getSelectedTaxonomyConcept, getDeprecationDate, getDeprecationLabel } from '../../redux/TaxonomyConceptSlice';
import { ActiveTaxonomyVersionInfo, getReferenceForVersion, selectCurrentVersion, TaxonomyTypeEnum, VersionRef } from '../../redux/TaxonomyVersionSlice';
import { Reference, getRefMap, getPartValue, selectCurrentReference, selectAllReferences, loadAllTaxonomyReferences, clearSelectedReference, setSelectedReference } from '../../redux/TaxonomyReferenceSlice';
import { setIsDirty, isAdmin } from '../../redux/MiscSlice';
import { ConceptNode, RestrictedAccountsFilter, NetworkConceptsFilter, selectFilteredAccountTree, loadAllAccounts } from '../../redux/AccountsSlice';
import { EditDeprecation, RelationshipInput, ConceptInput, getChangeConceptLabel, getChangeConceptId, getDeprecations, addDeprecation, getDeprecationFor, getEditableDeprecation, getConceptChanges, getRelationshipChanges } from '../../redux/TaxonomyChangesSlice';
import { SelectableListItem } from '../feature/BypassRequest';
import ConceptReferenceEditor from '../feature/ConceptReferenceEditor';
import ReferenceTypeAndPartManager from '../feature/ReferenceTypeAndPartManager';
import DeprecationReplacementSelector from '../feature/DeprecationReplacementSelector';
import EligibilityRulesEditor from '../feature/EligibilityRulesEditor';
import AccountDependenciesEditor from '../feature/AccountDependenciesEditor';
import AxisBuilder from '../feature/AxisBuilder';
import ConceptBreakdownManager from '../feature/ConceptBreakdownManager';
import ConceptInfoEditor from '../feature/ConceptInfoEditor';
import CreateConceptModal from '../layout/Modal/CreateConceptModal';
import { CreateConceptProps } from '../layout/Modal/CreateConceptModal';
import ChangeManager from '../feature/ChangeManager';
import utilityHelper from '../../utils/utilityHelper';
import { Node, Edge } from '../../redux/EntitiesSlice';
import { loadDefinitionNetworks, loadDefinitionNetworkRoot, Network, NetworkDetailsRequest, selectDefinitionNetworkRoot, selectDefinitionNetworks } from '../../redux/NetworkSlice';

export interface StyledSelectableListItem {
    parentItem: string | null;
    label: string;
    value: string;
    disabled: boolean;
    checked: boolean;
    className: string;
    networkid: string;
    children: Array<StyledSelectableListItem>;
}

export const buildTypedSelectionTree = function (itemMap: any, cnParent: ConceptNode | null, cnode: ConceptNode, selectedAccounts: string[], bIncludeDataType : boolean, bIncludeNetworkSource: boolean, bIsPublic : boolean): StyledSelectableListItem | null {
    // All nodes are selectable
    var disable = false;
    var conceptId = "none";
    var nid = "na"
    var dtype = "string"
    if (cnode && cnode.concept && cnode.concept.id) {
        conceptId = cnode.concept.id;
        dtype = cnode.concept.datatype;
        nid = cnode.networkId;
    }
    var itemValue = conceptId;
    var mapKey = nid + ">" + conceptId;
    switch (conceptId) {
        case "InstantTitle":
        case "DurationTitle":
            mapKey += nid;
            break;
    }
    if (mapKey && itemMap.hasOwnProperty(mapKey) && cnode.children.length > 0) {
        // We are already accounted for - proceed to the children
        var existingItem = itemMap[mapKey];
        if (!existingItem.children || existingItem.children.length === 0) {
            // Only add children if the item does not already have them
            for (var cnChild of cnode.children) {
                var childItem: StyledSelectableListItem | null = buildTypedSelectionTree(itemMap, cnode, cnChild, selectedAccounts, bIncludeDataType, bIncludeNetworkSource, bIsPublic);
                if (childItem) {
                    childItem.parentItem = existingItem.value;
                    existingItem.children.push(childItem);
                }
            }
        }
        // Don't return anything because we did not make a new item at this level
        var dupListItem: StyledSelectableListItem = {
            parentItem: null,
            label: existingItem.label,
            value: existingItem.value,
            disabled: disable,
            checked: false,
            children: existingItem.children,
            className: "",
            networkid: cnode.networkId
        }
        return dupListItem;
    }

    var bChecked = false;
    if (itemValue.length > 0 && (selectedAccounts.length > 0) && (selectedAccounts.indexOf(itemValue) >= 0)) {
        bChecked = true;
    }
    var typedItemValue = bIncludeDataType ? itemValue + "^" + dtype : itemValue;
    var cname = bIsPublic ? getPublicName(cnode.concept) : cnode.concept.name;
    if (!cnParent && bIncludeNetworkSource) { 
        cname += " (" + cnode.networkId + ")";
    }
    var listItem: StyledSelectableListItem = {
        parentItem: null,
        label: cname,
        value: typedItemValue,
        disabled: disable,
        checked: bChecked,
        children: [],
        className: "",
        networkid: cnode.networkId
    }
    itemMap[mapKey] = listItem;

    if (cnode.children) {
        for (var cnChild of cnode.children) {
            var childItem: StyledSelectableListItem | null = buildTypedSelectionTree(itemMap, cnode, cnChild, selectedAccounts, bIncludeDataType, bIncludeNetworkSource, bIsPublic);
            if (childItem) {
                childItem.parentItem = listItem.value;
                listItem.children.push(childItem);
            }
        }
    }
    return listItem;
}
export const buildSelectionTree = function (itemMap: any, cnParent: ConceptNode | null, cnode: ConceptNode, selectedAccounts: string[]): StyledSelectableListItem | null {
    return buildTypedSelectionTree(itemMap, cnParent, cnode, selectedAccounts, false, false, false);
}
const getSortKey = function (cn: ConceptNode): string {
    var sortKey: string = cn.concept.name;
    switch (cn.concept.name.toLocaleLowerCase()) {
        case "revenues":
            sortKey = "2";
            break;

        case "revenue (impact fees)":
            sortKey = "3";
            break;

        case "balance sheet":
            sortKey = "1";
            break;

        case "expenditures":
            sortKey = "4";
            break;

    }
    return sortKey;
}
export const getAccountsList = function (selectedAccount: string, accountTree: ConceptNode[] | null) {
    var alist: Array<SelectableListItem> = [];
    if (!accountTree) {
        return alist;
    }
    var itemMap: any = {};
    if (accountTree.length > 0) {
        var sortedTree: ConceptNode[] = [...accountTree];
        sortedTree.sort((a, b) => getSortKey(a).localeCompare(getSortKey(b)));
        for (var root of sortedTree) {
            var itemRoot = buildSelectionTree(itemMap, null, root, [selectedAccount]);
            // We only push a new root if it has children. No we are merging trees (e.g. Impact Fees with Revenues) the merged subtree will return only a single root node with no
            // children, so we don't add that to the list.
            if (itemRoot && itemRoot.children && itemRoot.children.length > 0) {
                alist.push(itemRoot);
            }
        }
    }
    return alist;
}
const processNewRelationship = function (itemMap: any, itemParentMap: any, newParentItemMap: any, rchg: RelationshipInput) {
    if (rchg.remove && rchg.remove === true) {
        // Only process positive relationships
        return;
    }
    if (itemMap.hasOwnProperty(rchg.fromId)) {
        // The simple case - there's a new concept attached to an existing parent - just assign the parent selection item to the new concept key
        itemParentMap[rchg.toId] = itemMap[rchg.fromId];
        return;
    }
    if (itemParentMap.hasOwnProperty(rchg.fromId)) {
        // There's a new concept that has another new concept as a parent
        var newParentSelectItem: StyledSelectableListItem | null = null;
        // First check to see if the new concept has already created it's selection item
        if (newParentItemMap.hasOwnProperty(rchg.fromId)) {
            newParentSelectItem = newParentItemMap[rchg.fromId];
        } else {
            newParentSelectItem = {
                parentItem: null,
                label: "",
                value: rchg.fromId,
                disabled: false,
                checked: false,
                children: [],
                networkid: rchg.networkId,
                className: "bi bi-file-plus-fill"
            }
            newParentItemMap[rchg.fromId] = newParentSelectItem;
        }
        itemParentMap[rchg.toId] = newParentSelectItem;
        return;
    }
    // This is an unrelated network change
}

const addChangeIcon = function (item: StyledSelectableListItem, itemMap: any, newItemMap: any) {
    if (item.className.length == 0) {
        item.className = "bi bi-hexagon-half";
    }
    if (item.parentItem == null) {
        return;
    }
    if (itemMap.hasOwnProperty(item.parentItem)) {
        var parent: StyledSelectableListItem = itemMap[item.parentItem];
        if (!(parent == null)) {
            addChangeIcon(parent, itemMap, newItemMap);
        }
    }
    if (newItemMap.hasOwnProperty(item.parentItem)) {
        var parent: StyledSelectableListItem = newItemMap[item.parentItem];
        if (!(parent == null)) {
            addChangeIcon(parent, itemMap, newItemMap);
        }
    }
}
const getVersionedAccountsListInner = function (versionInfo: any, selIdMap: any, selections: string[],
    accountTree: ConceptNode[] | null, conceptChanges: ConceptInput[], relationshipChanges: RelationshipInput[], bIncludeDataType : boolean, bIncludeNetworkSource : boolean, bIsPublic : boolean): Array<SelectableListItem> {
    var alist: Array<SelectableListItem> = [];
    if (!accountTree) {
        return alist;
    }
    var itemMap: any = {};
    if (accountTree.length > 0) {
        var sortedTree: ConceptNode[] = [...accountTree];
        sortedTree.sort((a, b) => getSortKey(a).localeCompare(getSortKey(b)));
        for (var root of sortedTree) {
            var itemRoot = buildTypedSelectionTree(itemMap, null, root, selections,bIncludeDataType, bIncludeNetworkSource, bIsPublic);
            // We only push a new root if it has children. No we are merging trees (e.g. Impact Fees with Revenues) the merged subtree will return only a single root node with no
            // children, so we don't add that to the list.
            if (itemRoot && itemRoot.children && itemRoot.children.length > 0) {
                alist.push(itemRoot);
            }
        }
    }
    if (conceptChanges.length > 0) {
        var itemParentMap: any = {};
        var newParentItems: any = {};
        relationshipChanges.map(rchg => processNewRelationship(itemMap, itemParentMap, newParentItems, rchg));
        conceptChanges.map(function (cchg: ConceptInput, index) {
            var chgId = cchg.id;
            if (itemParentMap.hasOwnProperty(chgId)) {
                var selItem: StyledSelectableListItem = itemParentMap[chgId];
                var listItem: StyledSelectableListItem | null = null;
                if (newParentItems.hasOwnProperty(chgId)) {
                    // we have already made the new parent's selection item, so this only needs to be updated.
                    listItem = newParentItems[chgId];
                    if (listItem) {
                        listItem.parentItem = selItem.value;
                        listItem.label = getChangeConceptLabel(cchg, "label");
                        listItem.checked = (selIdMap.hasOwnProperty(chgId));
                    }
                } else {
                    listItem = {
                        parentItem: selItem.value,
                        label: getChangeConceptLabel(cchg, "label"),
                        value: chgId,
                        disabled: false,
                        checked: (selIdMap.hasOwnProperty(chgId)),
                        children: [],
                        networkid: selItem.networkid,
                        className: "bi bi-file-plus-fill"
                    }
                }
                if (listItem) {
                    selItem.children.push(listItem);
                }
                addChangeIcon(selItem, itemMap, newParentItems);
                // NOTE - Even though we have switched from code to id for the item values, the pattern for UAS account code ids is ccc_nnnnnn_{name of element}, 
                // e.g.Rev_348110_CountyCourtCriminalFilingFees so the sorting at the sibling level will match sorting by code alone.
                selItem.children.sort((a, b) => a.value.localeCompare(b.value));
            }
        });
    }
    return alist;
}
export const getVersionedAccountsList = function (versionInfo: any, selectedAccount: DtsConcept | null,
    accountTree: ConceptNode[] | null, conceptChanges: ConceptInput[], relationshipChanges: RelationshipInput[], bDepsOnly: boolean, bIncludeNetworkSource : boolean, bIsPublic : boolean): Array<SelectableListItem> {
    if (!accountTree) {
        return [];
    }
    var selIdMap: any = {};
    var selections: string[] = [];
    if (selectedAccount && selectedAccount.concept && selectedAccount.concept.id.length > 0) {
        var selCode = selectedAccount.concept.id;
        selections = [selCode];
        selIdMap[selCode] = true;
    }
    var result: Array<SelectableListItem> = [];
    if (bDepsOnly) {
        result = getVersionedAccountsWithDependencies(accountTree, selections, versionInfo, conceptChanges, relationshipChanges);
    } else {
        result = getVersionedAccountsListInner(versionInfo, selIdMap, selections, accountTree, conceptChanges, relationshipChanges, false, bIncludeNetworkSource, bIsPublic);
    }
    return result;
}
export const getVersionedAccountsForDependencies = function (accountTree: ConceptNode[] | null, selections: string[],
    versionInfo: any, conceptChanges: ConceptInput[], relationshipChanges: RelationshipInput[]): Array<SelectableListItem> {
    if (!accountTree) {
        return [];
    }
    var selIdMap: any = {};
    var idSelections: string[] = [];
    selections.map((s) => {
        var cid = s.split("^")[0]; 
        selIdMap[cid] = true;
        idSelections.push(cid);
    } );
    return getVersionedAccountsListInner(versionInfo, selIdMap, idSelections, accountTree, conceptChanges, relationshipChanges, true, false, false);
}
const convertNodeToConceptNode = function (cnParent: ConceptNode | null, n: Node, net : Network) : ConceptNode {
    var cn: ConceptNode = {
        networkId: net.name,
        concept: n.concept,
        children: [],
        referenceTypes: null
    }
    if (cnParent) {
        cnParent.children.push(cn);
    }
    if (n.outgoing) {
        n.outgoing.map((e: any) => convertNodeToConceptNode(cn, e.to, net));
    }
    return cn;
}
export const convertNetworksToConceptTrees = function(networks: Network[] | null) {
    if (!networks) {
        return [];
    }
    var conceptTree: ConceptNode[] = [];
    for (var net of networks) {
        for (var root of net.roots) {
            if (root.concept.name === "Line items") {
                continue;
            }
            var cn: ConceptNode = convertNodeToConceptNode(null, root, net);
            conceptTree.push(cn);
        }
    }
    return conceptTree;

}

export const addTypedSelectionWithDependencies = function (itemMap: any, cnParent: ConceptNode | null, cnode: ConceptNode, selectedAccounts: string[], alist: Array<SelectableListItem>): StyledSelectableListItem | null {
    // All nodes are selectable
    var disable = false;
    var conceptId = "none";
    var nid = "na"
    var dtype = "string"
    if (cnode && cnode.concept && cnode.concept.id) {
        conceptId = cnode.concept.id;
        dtype = cnode.concept.datatype;
        nid = cnode.networkId;
    }
    var itemValue = conceptId;
    var mapKey = nid + ">" + conceptId;
    switch (conceptId) {
        case "InstantTitle":
        case "DurationTitle":
            mapKey += nid;
            break;
    }
    if (mapKey && itemMap.hasOwnProperty(mapKey)) {
        // We are already accounted for - proceed to the children
        var existingItem = itemMap[mapKey];
        for (var cnChild of cnode.children) {
            addTypedSelectionWithDependencies(itemMap, cnode, cnChild, selectedAccounts, alist);
        }
        // Don't return anything because we did not make a new item at this level
        return null;
    }

    var bChecked = false;
    if (itemValue.length > 0 && (selectedAccounts.length > 0) && (selectedAccounts.indexOf(itemValue) >= 0)) {
        bChecked = true;
    }
    var typedItemValue = itemValue;
    var listItem: StyledSelectableListItem = {
        parentItem: null,
        label: cnode.concept.name,
        value: typedItemValue,
        disabled: disable,
        checked: bChecked,
        children: [],
        className: "",
        networkid: cnode.networkId
    }
    itemMap[mapKey] = listItem;
    if (cnode.referenceTypes && cnode.referenceTypes.indexOf("accountDependency") >= 0) {
        alist.push(listItem);
    }

    if (cnode.children) {
        for (var cnChild of cnode.children) {
            addTypedSelectionWithDependencies(itemMap, cnode, cnChild, selectedAccounts, alist);
        }
    }
    return listItem;
}
const getVersionedAccountsListWithDependenciesInner = function (versionInfo: any, selIdMap: any, selections: string[],
    accountTree: ConceptNode[] | null, conceptChanges: ConceptInput[], relationshipChanges: RelationshipInput[]): Array<SelectableListItem> {
    var alist: Array<SelectableListItem> = [];
    if (!accountTree) {
        return alist;
    }
    var itemMap: any = {};
    if (accountTree.length > 0) {
        var sortedTree: ConceptNode[] = [...accountTree];
        sortedTree.sort((a, b) => getSortKey(a).localeCompare(getSortKey(b)));
        for (var root of sortedTree) {
            addTypedSelectionWithDependencies(itemMap, null, root, selections, alist);
        }
    }
    if (conceptChanges.length > 0) {
        var itemParentMap: any = {};
        var newParentItems: any = {};
        relationshipChanges.map(rchg => processNewRelationship(itemMap, itemParentMap, newParentItems, rchg));
        conceptChanges.map(function (cchg: ConceptInput, index) {
            var chgId = cchg.id;
            if (itemParentMap.hasOwnProperty(chgId)) {
                var selItem: StyledSelectableListItem = itemParentMap[chgId];
                var listItem: StyledSelectableListItem | null = null;
                if (newParentItems.hasOwnProperty(chgId)) {
                    // we have already made the new parent's selection item, so this only needs to be updated.
                    listItem = newParentItems[chgId];
                    if (listItem) {
                        listItem.parentItem = selItem.value;
                        listItem.label = getChangeConceptLabel(cchg, "label");
                        listItem.checked = (selIdMap.hasOwnProperty(chgId));
                    }
                } else {
                    listItem = {
                        parentItem: selItem.value,
                        label: getChangeConceptLabel(cchg, "label"),
                        value: chgId,
                        disabled: false,
                        checked: (selIdMap.hasOwnProperty(chgId)),
                        children: [],
                        networkid: selItem.networkid,
                        className: "bi bi-file-plus-fill"
                    }
                }
                if (listItem) {
                    selItem.children.push(listItem);
                }
                addChangeIcon(selItem, itemMap, newParentItems);
                // NOTE - Even though we have switched from code to id for the item values, the pattern for UAS account code ids is ccc_nnnnnn_{name of element}, 
                // e.g.Rev_348110_CountyCourtCriminalFilingFees so the sorting at the sibling level will match sorting by code alone.
                selItem.children.sort((a, b) => a.value.localeCompare(b.value));
            }
        });
    }
    alist.sort((a, b) => a.label.localeCompare(b.label));
    return alist;
}

export const getVersionedAccountsWithDependencies = function (accountTree: ConceptNode[] | null, selections: string[],
    versionInfo: any, conceptChanges: ConceptInput[], relationshipChanges: RelationshipInput[]): Array<SelectableListItem> {
    if (!accountTree) {
        return [];
    }
    var filteredAccountTree: ConceptNode[] = [];
    accountTree.map(root => root.networkId.indexOf("functional") >= 0 ? "" : filteredAccountTree.push(root));
    var selIdMap: any = {};
    var idSelections: string[] = [];
    selections.map((s) => {
        var cid = s.split("^")[0];
        selIdMap[cid] = true;
        idSelections.push(cid);
    });
    return getVersionedAccountsListWithDependenciesInner(versionInfo, selIdMap, idSelections, filteredAccountTree, conceptChanges, relationshipChanges);
}
export const getAccountsListMulti = function (selectedAccounts: string[], accountTree: ConceptNode[] | null): StyledSelectableListItem[] {
    var alist: Array<StyledSelectableListItem> = [];
    if (!accountTree) {
        return alist;
    }
    if (accountTree.length > 0) {
        var itemMap: any = {};
        for (var root of accountTree) {
            var itemRoot = buildSelectionTree(itemMap, null, root, selectedAccounts);
            // We only push a new root if it has children. No we are merging trees (e.g. Impact Fees with Revenues) the merged subtree will return only a single root node with no
            // children, so we don't add that to the list.
            if (itemRoot && itemRoot.children && itemRoot.children.length > 0) {
                alist.push(itemRoot);
            }
        }
    }
    return alist;
}


const ConceptManagementCore = (props: {
    taxonomyType: TaxonomyTypeEnum
}) => {
    const dispatch = useAppDispatch();
    const isAdminUser: boolean = useAppSelector(isAdmin);
    var references: Array<Reference> = useAppSelector(selectAllReferences);
    var deprecations: EditDeprecation[] = useAppSelector(getDeprecations);
    var selectedReference: Reference | null = useAppSelector(selectCurrentReference);
    var versionInfo: ActiveTaxonomyVersionInfo | null = useAppSelector(selectCurrentVersion);
    var versionedConcept: DtsConcept | null = useAppSelector(getSelectedTaxonomyConcept);
    var concept: Concept | null = versionedConcept ? versionedConcept.concept : null;
    var definitionNetworks: Network[] = useAppSelector(selectDefinitionNetworks);
    const accountTree = useAppSelector(selectFilteredAccountTree);
    const conceptChanges = useAppSelector(getConceptChanges);
    const relationshipChanges = useAppSelector(getRelationshipChanges);
    const [selectedAccount, setSelectedAccount] = useState("");
    const [accountsLoaded, setAccountsLoaded] = useState(false);
    const [activeTab, setActiveTab] = useState("1");
    const [conceptLabel, setConceptLabel] = useState(concept ? concept.name : "");
    const [conceptId, setConceptId] = useState(concept ? concept.id : "");
    const [conceptCode, setConceptCode] = useState(concept ? concept.code : "");
    const [documentation, setDocumentation] = useState(concept ? concept.documentation : "");
    const [versionYear, setVersionYear] = useState(0);
    const [versionType, setVersionType] = useState("");
    const [selectedAccountStatus, setSelectedAccountStatus] = useState("");
    const [selectedAccountDeprecationDate, setSelectedAccountDeprecationDate] = useState("");
    const [selectedAccountDeprecationLabel, setSelectedAccountDeprecationLabel] = useState("");
    const [referencesYear, setReferencesYear] = useState(-1);
    const [deprecationDirty, setDeprecationDirty] = useState(false);
    const [isNewAccount, setIsNewAccount] = useState(false);
    const tabulatorRef = useRef(null);
    const [modalCreateConceptIsOpen, setModalCreateConceptIsOpen] = useState(false);
    const [selectedNetwork, setSelectedNetwork] = useState("");
    const [accountDependenciesOnly, setAccountDependenciesOnly] = useState<boolean>(false);
    const [networkMode, setNetworkMode] = useState<boolean>(false);
    const [isAbstract, setIsAbstract] = useState<boolean>(true);

    //    const [fullAccountsList, setFullAccountsList] = useState<SelectableListItem[]>(getAccountsList((versionedConcept && versionedConcept.concept) ? versionedConcept.concept.code : "" , accountTree));

    const emptyDeprecation = { conceptId: "", deprecationDate: "", reason: "", replacements: [], replacementType: "", new: false, dirty: false };


    const isCurrentConceptAbstract = function (c: DtsConcept | null) : boolean {
        if (!c) {
            return true;
        }
        if (!c.concept) {
            return true;
        }
        if (c.concept.isAbstract === null) {
            return true;
        }
        return c.concept.isAbstract === true;
    }
    const getCurrentDeprecation = function (c: DtsConcept | null): EditDeprecation {
        if (!c || !c.concept) {
            return emptyDeprecation;
        }
        var concept = c.concept;
        var pendingDep: EditDeprecation | null = getDeprecationFor(deprecations, concept.id);
        if (!pendingDep) {
            if (c.concept.deprecation) {
                pendingDep = getEditableDeprecation(concept);
            } else {
                var deprecationDate: string | null = getDeprecationDate(concept);
                if (deprecationDate) {
                    var deprecationReason = getDeprecationLabel(concept);
                    if (!deprecationReason) {
                        deprecationReason = "";
                    }
                    // Make one up around the existing deprecation model
                    pendingDep = {
                        conceptId: concept.id,
                        reason: deprecationReason,
                        deprecationDate: deprecationDate,
                        replacements: [],
                        replacementType: "",
                        new: false,
                        dirty: false
                    }
                }
            }
        }
        if (!pendingDep) {
            return emptyDeprecation;
        }
        return pendingDep;
    }
    const [deprecation, setDeprecation] = useState<EditDeprecation>(getCurrentDeprecation(versionedConcept));

    const resetDeprecation = function () {
        setDeprecation(emptyDeprecation);
        setDeprecationDirty(false);
        setSelectedAccountStatus("");
        setSelectedAccountDeprecationDate("");
        setSelectedAccountDeprecationLabel("");
    }

    const updateCurrentDeprecationForConcept = function (c: DtsConcept) {
        var ed: EditDeprecation = getCurrentDeprecation(c);
        setDeprecation(ed);
        setDeprecationDirty(false);
        if (ed.deprecationDate !== undefined && ed.deprecationDate.length > 0) {
            setSelectedAccountStatus("(deprecated: " + ed.deprecationDate + ")");
            if (ed.deprecationDate != undefined) {
                setSelectedAccountDeprecationDate(ed.deprecationDate);
            }

            var depLabel = ed.reason;
            if (depLabel) {
                setSelectedAccountDeprecationLabel(depLabel);
            } else {
                setSelectedAccountDeprecationLabel("");
            }
        } else {
            setSelectedAccountStatus("");
            setSelectedAccountDeprecationDate("");
            setSelectedAccountDeprecationLabel("");
        }
    }


    const toggle = (tab: string) => {
        if (activeTab !== tab) setActiveTab(tab);
    }

    const activeClass = (tab: string) => {
        return (tab === activeTab) ? "active" : "";
    }

    const isValid = function (field: string) {
        var result: boolean = true;
        switch (field) {
            case "label":
                if (!conceptLabel || conceptLabel.length === 0) {
                    result = false;
                }
                break;
            case "documentation":
                break;
        }
        return result;
    }

    const isDeprecationDirty = function (dep: EditDeprecation): boolean {
        if (!dep) {
            return false;
        }
        if (dep.dirty == null) {
            return false;
        }
        return dep.dirty === true;
    }

    const updateDeprecation = function (dep: EditDeprecation): EditDeprecation {
        setDeprecation(dep);
        setDeprecationDirty(true);
        return dep;
    }

    const setDeprecationDate = function (newDate: string) {
        setSelectedAccountDeprecationDate(newDate);
        setDeprecationDirty(true);
    }

    const setDeprecationLabel = function (newLabel: string) {
        setSelectedAccountDeprecationLabel(newLabel);
        setDeprecationDirty(true);
    }

    const handleDepsOnly = function (e: any) {
        setAccountDependenciesOnly(e.target.checked);
        if (e.target.checked) {
            setNetworkMode(false);
        }
        dispatch(resetTaxonomyConceptState());

    }
    const handleNetworkMode = function (e: any) {
        setNetworkMode(e.target.checked);
        if (e.target.checked) {
            setAccountDependenciesOnly(false);
        }
        dispatch(resetTaxonomyConceptState());

    }
    const handleCancelDeprecationClick = () => {
        //        switchToLastSavedReference(selectedReference);
    }

    const handleSaveDeprecationClick = () => {
        if (!versionedConcept || !versionedConcept.concept) {
            return;
        }
        var dep: EditDeprecation = {
            ...deprecation,
            conceptId: versionedConcept.concept.id,
            deprecationDate: selectedAccountDeprecationDate,
            reason: selectedAccountDeprecationLabel,
            dirty: false
        };
        setDeprecation(dep);
        dispatch(addDeprecation(dep));
    }

    const handleUnitScopeChange = function (currentNode: any, selectedNodes: any): boolean {
        //        setIsNewAccount(true);
        return true;
    }

    const handleNewAccountClick = () => {
        if (!versionedConcept) {
            return;
        }
        setModalCreateConceptIsOpen(true);
    }

    const createParentRelationship = function (parent: Concept): RelationshipInput {
        if (!parent) {
            return {
                fromId: "none",
                networkId: "none",
                role: "parent-child",
                toId: "",
                remove: false
            };
        }
        // Since we are creating a new concept in the context of an existing parent, we already have the network that we need for the 
        // new parent relationship since the Account dropdown is organized by network
        var nid = selectedNetwork;
        if (!nid) {
            // If this concept is already deprecated, it likely won't be in any networks
            return {
                fromId: "none",
                networkId: "none",
                role: "parent-child",
                toId: "",
                remove: false
            };
        }
        // The following code is default that should not be required since nid will already hold the parent's network id. 
        if (nid.length === 0) {
            if (parent.code < "3") {
                nid = "uas_006_balance-sheet";
            } else if (parent.code < "5") {
                nid = "uas_020_revenue";
            } else if (parent.code < "9") {
                nid = "uas_034_expenditures";
            }
        }
        var rel: RelationshipInput = {
            fromId: parent.id,
            networkId: nid,
            role: "parent-child",
            toId: "",
            remove: false
        };
        return rel;
    }

    const ddhandleChange = function (currentNode: any, selectedNodes: any) {
        if (selectedNodes.length === 0) {
            setSelectedAccount("");
            dispatch(setSelectedTaxonomyConcept(null));
            setActiveTab('ReferenceManagement');
            return;
        }
        var selNode = selectedNodes[0];
        var conceptId = selNode.value;
        setSelectedAccount(conceptId);
        setSelectedNetwork(selNode.networkid);
        if (conceptId.indexOf(".") === 3 && conceptId.length === 6) {
            conceptId = conceptId.substring(0, 3);
        }
        var vconcept: DtsConcept | null = null;
        if (conceptChanges && versionInfo) {
            var cchg: ConceptInput | undefined = conceptChanges.find((cc: any) => cc.id === conceptId);
            if (cchg == null) {
                vconcept = null;
            } else {
                vconcept = {
                    parent: null,
                    concept: {
                        id: cchg.id,
                        name: getChangeConceptLabel(cchg, "label"),
                        code: getChangeConceptLabel(cchg, "code"),
                        documentation: "",
                        datatype: cchg.datatype,
                        deprecation: null,
                        domainDatatype: "",
                        labels: [],
                        references: [],
                        isAbstract: cchg.isAbstract,
                        isCredit: cchg.isCredit,
                        periodtype: cchg.periodtype,
                        isDeprecated: false,
                    },
                    year: versionInfo.year,
                    entityScope: null,
                    dimensionalBreakdowns: null,
                    dimensionalScope: null,
                    dependencies: null,
                    new: true
                }
            }
        }
        var ref: ConceptRef = {
            conceptId: conceptId,
            taxonomyType: utilityHelper.getTaxonomyTypeFromVersion(versionInfo),
            afrYear: versionInfo ? versionInfo.year : 2022
        }
        setDeprecation(emptyDeprecation);
        if (vconcept) {
            dispatch(setSelectedTaxonomyConcept(vconcept));
            dispatch(clearSelectedReference());
        } else {
            dispatch(loadTaxonomyConcept(ref)).then((result: any) => {
                dispatch(clearSelectedReference())
            });
        }
        setActiveTab('1');
    }

    const handleCloseConceptModal = function (event: any) {
        if (event && event.hasOwnProperty("newconcept")) {
            setDeprecation(emptyDeprecation);
            var newVersionedConcept: DtsConcept = {
                parent: versionedConcept,
                concept: event.newconcept,
                year: versionInfo ? versionInfo.year : 2022,
                entityScope: null,
                dimensionalBreakdowns: null,
                dimensionalScope: null,
                dependencies: null,
                new: true
            }
            dispatch(setSelectedTaxonomyConcept(newVersionedConcept));
            dispatch(clearSelectedReference());
        }
        setModalCreateConceptIsOpen(false);
        //        dispatch(clearEditReference);
    }

    const handleCodeValidation = function (value: string): boolean {
        return value.length > 0;
    }


    const leftTopMarginStyle = {
        marginTop: "1em",
        marginLeft: "1em"
    }

    const flexRightChild = {
        marginLeft: 'auto',
        marginRight: '1em'
    }

    const leftMarginStyle = {
        marginLeft: '1em'
    }

    useEffect(() => {
        var vYear: number = 2022;
        var vType: TaxonomyTypeEnum = props.taxonomyType;
        var published : boolean = true;
        if (versionInfo) {
            vYear = versionInfo.year;
            if (versionInfo.type) {
                vType = versionInfo.type;
            }
            published = versionInfo.isPublished;
        }
        var vInfo: ActiveTaxonomyVersionInfo = {
            year: vYear,
            type: vType,
            id: "local",
            periods: null,
            isPublished: published
        };
        var vRef: VersionRef = {
            year: vYear,
            type: vType
        }
        setVersionYear(vYear);
        setVersionType(utilityHelper.toTitleCase(vType));
        setSelectedAccount("");
        setActiveTab('ReferenceManagement');
        var accountQuery: NetworkConceptsFilter = {
            nameFilter: "*Title+",
            taxonomyVersion: getReferenceForVersion(vInfo, -1)
        }
        dispatch(loadAllAccounts(accountQuery));
        //        setFullAccountsList(getAccountsList("", accountTree));
        dispatch(resetTaxonomyConceptState());
        dispatch(loadAllTaxonomyReferences(vRef));
        setAccountsLoaded(true);
        resetDeprecation();
    }, []);

    useEffect(() => {
        var vYear: number = 2022;
        var vType: TaxonomyTypeEnum = props.taxonomyType;
        var published : boolean = true;
        if (versionInfo) {
            vYear = versionInfo.year;
            if (versionInfo.type) {
                vType = versionInfo.type;
            }
            published = versionInfo.isPublished;
        }
        var vInfo: ActiveTaxonomyVersionInfo = {
            year: vYear,
            type: vType,
            id: "local",
            periods: null,
            isPublished: published
        };
        var vRef: VersionRef = {
            year: vYear,
            type: vType
        }
        setVersionYear(vYear);
        setVersionType(utilityHelper.toTitleCase(vType));
        setSelectedAccount("");
        setActiveTab('ReferenceManagement');
        var accountQuery: NetworkConceptsFilter = {
            nameFilter: "*Title+",
            taxonomyVersion: getReferenceForVersion(vInfo, -1)
        }
        dispatch(loadAllAccounts(accountQuery));
        dispatch(loadDefinitionNetworks);
        //        setFullAccountsList(getAccountsList("", accountTree));
        dispatch(resetTaxonomyConceptState());
        dispatch(loadAllTaxonomyReferences(vRef));
        setAccountsLoaded(true);
        resetDeprecation();
    }, [versionInfo]);

    useEffect(() => {
        setIsAbstract(isCurrentConceptAbstract(versionedConcept));
        if (versionedConcept) {
            setActiveTab('1');
            var dtsConcept: DtsConcept = versionedConcept;
            updateCurrentDeprecationForConcept(dtsConcept);
        } else {
            setActiveTab('ReferenceManagement');
            resetDeprecation();
        }
    }, [concept, versionedConcept]);

    useEffect(() => {
        setSelectedAccount("");
        setActiveTab('ReferenceManagement');
        resetDeprecation();
    }, [networkMode]);

    var tabledata = [];
    const accountSelector = useMemo(() => {
        return (
            <DropdownTreeSelect
                data={getVersionedAccountsList(versionInfo, versionedConcept, accountTree, conceptChanges, relationshipChanges, accountDependenciesOnly, false, false)}
                mode="radioSelect" keepTreeOnSearch={true}
                onChange={ddhandleChange}
                texts={{ placeholder: "Choose Account..." }} />
        )
    }, [conceptChanges, accountTree, accountDependenciesOnly]);

    const axisAndMemberSelector = useMemo(() => {
        return (
            <DropdownTreeSelect
                data={getVersionedAccountsList(versionInfo, versionedConcept, convertNetworksToConceptTrees(definitionNetworks), conceptChanges, relationshipChanges, accountDependenciesOnly, true, false)}
                mode="radioSelect" keepTreeOnSearch={true}
                onChange={ddhandleChange}
                texts={{ placeholder: "Choose Member..." }} />
        )
    }, [conceptChanges, definitionNetworks]);



    return (
        <Page>
            <Row>
                <Col sm="12">
                    <h2>{versionType} {networkMode ? "Dimension Member" : "Account"} and Reference Management ({versionYear})</h2>
                    <Row>
                        <Col sm="1"></Col>
                        <Col >                            
                            <Input type="checkbox"
                                
                                name="deps_only"
                                onChange={(e) => handleDepsOnly(e)}
                                checked={accountDependenciesOnly}
                            />{' '} <b>Show only accounts with dependencies</b>
                        </Col>
                        <Col >
                            <Input type="checkbox"

                                name="networkMode"
                                onChange={(e) => handleNetworkMode(e)}
                                checked={networkMode}
                            />{' '} <b>Use networks and members instead of accounts</b>
                        </Col>
                    </Row>
                    <Row>
                        <h5>
                            <React.Fragment>
                                {networkMode ?
                                    axisAndMemberSelector
                                    :
                                    accountSelector
                                }
                            </React.Fragment>
                        </h5>
                        <h4 style={leftTopMarginStyle}>{selectedAccountStatus}</h4>
                        <div style={flexRightChild} >
                            <Button type="button" style={leftMarginStyle} className="taxedit" disabled={!versionedConcept} onClick={handleNewAccountClick}>New Account</Button>
                        </div>
                    </Row>
                    <div>
                        {versionedConcept
                            ?
                            <Nav tabs>
                                <NavItem>
                                    <NavLink
                                        className={activeClass('1')}
                                        onClick={() => { toggle('1'); }}
                                    >
                                        Info and Labels
                                                        </NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink
                                        className={activeClass('2')}
                                        onClick={() => { toggle('2'); }}
                                    >
                                        Using References
                                    </NavLink>
                                </NavItem>
                                {!isAbstract &&
                                    <NavItem>
                                        <NavLink
                                            className={activeClass('3')}
                                            onClick={() => { toggle('3'); }}
                                        >
                                            Eligibility Rules
                                        </NavLink>
                                    </NavItem>
                                }
                                <NavItem>
                                    <NavLink
                                        className={activeClass('4')}
                                        onClick={() => { toggle('4'); }}
                                    >
                                        Deprecation
                                    </NavLink>
                                </NavItem>
                                {!isAbstract &&
                                    <NavItem>
                                        <NavLink
                                            className={activeClass('5')}
                                            onClick={() => { toggle('5'); }}
                                        >
                                            Breakdowns
                                        </NavLink>
                                    </NavItem>
                                }
                                {!isAbstract &&
                                    <NavItem>
                                        <NavLink
                                            className={activeClass('6')}
                                            onClick={() => { toggle('6'); }}
                                        >
                                            Account Dependencies
                                        </NavLink>
                                    </NavItem>
                                }
                                <NavItem>
                                    <NavLink
                                        className={activeClass('ReferenceManagement')}
                                        style={{ color: 'black', backgroundColor: 'beige' }} activeStyle={{ color: 'white', backgroundColor: 'green' }}
                                        onClick={() => { toggle('ReferenceManagement'); }}
                                    >
                                        Reference Management
                                    </NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink
                                        className={activeClass('9')}
                                        style={{ color: 'black', backgroundColor: 'beige' }} activeStyle={{ color: 'white', backgroundColor: 'green' }}
                                        onClick={() => { toggle('9'); }}
                                    >
                                        Change Management
                                    </NavLink>
                                </NavItem>
                            </Nav>
                            :
                            <Nav tabs>
                                <NavItem>
                                    <NavLink
                                        className={activeClass('7')}
                                        style={{ color: 'black', backgroundColor: 'beige' }} activeStyle={{ color: 'white', backgroundColor: 'green' }}
                                        onClick={() => { toggle('7'); }}
                                    >
                                        Reference Management
                                                    </NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink
                                        className={activeClass('9')}
                                        style={{ color: 'black', backgroundColor: 'beige' }} activeStyle={{ color: 'white', backgroundColor: 'green' }}
                                        onClick={() => { toggle('9'); }}
                                    >
                                        Change management
                                                </NavLink>
                                </NavItem>
                            </Nav>
                        }
                        <TabContent activeTab={activeTab}>
                            <TabPane tabId="1">
                                <Row>
                                    <Col sm="12">
                                        <h5>
                                            <p>Use this screen to modify labels, documentation and references</p>
                                        </h5>
                                        <ConceptInfoEditor currentVersionedConcept={versionedConcept} selectedNetworkId={selectedNetwork} />
                                    </Col>
                                </Row>
                            </TabPane>
                            <TabPane tabId="2">
                                <Row>
                                    <Col sm="12">
                                        {(versionedConcept && versionedConcept.concept)
                                            ?
                                            <ConceptReferenceEditor currentVersionedConcept={versionedConcept} />
                                            :
                                            <div></div>
                                        }
                                    </Col>
                                </Row>
                            </TabPane>
                            {!isAbstract &&
                                <TabPane tabId="3">
                                    <Row>
                                        <Col sm="12">
                                            <h5>
                                                <p>Use this screen to modify eligibility rules e.g. scope, dependency</p>
                                            </h5>
                                            <EligibilityRulesEditor
                                                currentConcept={versionedConcept}
                                                accountTree={accountTree}
                                                handleUnitScopeChange={handleUnitScopeChange}
                                                handleDependencyAccountList={getVersionedAccountsForDependencies}
                                                conceptChanges={conceptChanges}
                                                relationshipChanges={relationshipChanges}
                                                versionInfo={versionInfo}
                                            />
                                        </Col>
                                    </Row>
                                </TabPane>
                            }
                            <TabPane tabId="4">
                                <Row>
                                    <Col sm="12">
                                        <Row>
                                            <h5>
                                                <p>Deprecate the selected account</p>
                                            </h5>
                                            <div style={flexRightChild} >
                                                <Button type="button" style={leftMarginStyle} className="taxedit" disabled={!deprecationDirty} onClick={handleCancelDeprecationClick}>Undo</Button>
                                                <Button type="button" style={leftMarginStyle} className="taxedit" disabled={!deprecationDirty} onClick={handleSaveDeprecationClick}>Save</Button>
                                            </div>
                                        </Row>
                                        <FormGroup>

                                            <Row>
                                                <Col className="text-md-right" >
                                                    <Label><b>Deprecation date:</b></Label>
                                                </Col>
                                                <Col lg="9">
                                                    <Input type="date" value={selectedAccountDeprecationDate}
                                                        onChange={(e) => setDeprecationDate(e.currentTarget.value)}
                                                        valid={isValid("deprecationDate")}
                                                        disabled={!versionedConcept}
                                                        invalid={(!isValid("deprecationDate"))} />
                                                    <FormFeedback>Subject is required.</FormFeedback>
                                                </Col>
                                            </Row>

                                        </FormGroup>
                                        <FormGroup>
                                            <Row>
                                                <Col className="text-md-right" >
                                                    <Label><b>Deprecation comments:</b></Label>
                                                </Col>
                                                <Col md="9">
                                                    <Input type="textarea" value={selectedAccountDeprecationLabel} rows="10"
                                                        onChange={(e) => setDeprecationLabel(e.currentTarget.value)}
                                                        disabled={!versionedConcept}
                                                        valid={isValid("deprecationLabel")}
                                                        invalid={!isValid("deprecationLabel")}
                                                    />
                                                    <FormFeedback>Email is required.</FormFeedback>
                                                </Col>
                                            </Row>
                                        </FormGroup>
                                        <Row>
                                            <Col className="text-md-right" >
                                                <Label><b>Substitutions:</b></Label>
                                            </Col>
                                            <Col lg="9">
                                                {versionedConcept
                                                    ?
                                                    <DeprecationReplacementSelector currentConcept={versionedConcept}
                                                        deprecation={deprecation}
                                                        prompt="Add Substitution..."
                                                        mode="multiSelect"
                                                        updateDeprecation={updateDeprecation} />
                                                    : <div></div>
                                                }
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>
                            </TabPane>
                            {!isAbstract &&
                                <TabPane tabId="5">
                                    <Row>
                                        <Col sm="12">
                                            <h5>
                                                <p>Use this to apply breakdowns</p>
                                            </h5>
                                            <ConceptBreakdownManager />
                                        </Col>
                                    </Row>
                                </TabPane>
                            }
                            {!isAbstract &&
                                <TabPane tabId="6">
                                    <Row>
                                        <Col sm="12">
                                            <h5>
                                                <p>Use this screen to modify dependency rules e.g. scope, dependency</p>
                                            </h5>
                                            <AccountDependenciesEditor
                                                currentConcept={versionedConcept}
                                                accountTree={accountTree}
                                                handleUnitScopeChange={handleUnitScopeChange}
                                                handleDependencyAccountList={getVersionedAccountsForDependencies}
                                                conceptChanges={conceptChanges}
                                                relationshipChanges={relationshipChanges}
                                                versionInfo={versionInfo}
                                            />
                                        </Col>
                                    </Row>
                                </TabPane>
                            }
                            <TabPane tabId="7">
                                <Row>
                                    <Col sm="12">
                                        <ReferenceTypeAndPartManager />
                                    </Col>
                                </Row>
                            </TabPane>
                            <TabPane tabId="9">
                                <Row>
                                    <Col sm="12">
                                        <h5>
                                            <Card>
                                                <CardTitle style={{ color: 'black', backgroundColor: 'beige' }}>Change Manager</CardTitle>
                                                <CardBody>Review and undo pending changes to the Taxonomy</CardBody>
                                            </Card>
                                            <ChangeManager />
                                        </h5>
                                    </Col>
                                </Row>
                            </TabPane>
                        </TabContent>
                    </div>
                </Col>
            </Row>
            {versionedConcept && versionedConcept.concept && (<CreateConceptModal
                isOpen={modalCreateConceptIsOpen}
                parentName={versionedConcept.concept.name}
                parentCode={versionedConcept.concept.code}
                parentRelationship={createParentRelationship(versionedConcept.concept)}
                conceptType="Account"
                codeValidator={handleCodeValidation}
                onCloseModal={handleCloseConceptModal}
                conceptChanges={conceptChanges}
                accountTree={accountTree}
                isMemberConcept={false} />)}
        </Page>
    );
}
export default ConceptManagementCore;